import { Stack, Typography } from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import { useState } from "react";
import JsonCodeField from "./JsonCodeField";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
  loading?: boolean;
}

/**
 * Manual-run payload prompt for webhook-driven integrations: their source
 * step consumes the inbound event, so an empty run would be guaranteed to
 * fail. The operator pastes the JSON envelope a webhook would deliver
 * (e.g. to replay an event).
 */
export default function RunNowDialog({
  open,
  onClose,
  onSubmit,
  loading = false,
}: Props) {
  const [draft, setDraft] = useState("{}");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    let payload: Record<string, unknown>;
    try {
      payload = draft.trim() ? JSON.parse(draft) : {};
    } catch {
      setError("Invalid JSON.");
      return;
    }
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      setError("The payload must be a JSON object.");
      return;
    }
    onSubmit(payload);
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Run with event payload"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSubmit}
            disabled={loading}
          >
            Run
          </CustomButton>
        </>
      }
    >
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          This integration is event-driven: its source step consumes the webhook
          payload. Provide the JSON event a webhook would deliver (e.g. to
          replay one).
        </Typography>
        <JsonCodeField
          value={draft}
          onChange={(v) => {
            setDraft(v);
            setError(null);
          }}
          error={error}
          rows={10}
        />
      </Stack>
    </AppDialog>
  );
}
